import React, { useMemo } from "react";
import { useScheduleData } from "./ScheduleDataContext";

function extractStringDeep(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "object") {
    const keys = ["value", "name", "label", "display", "text", "title"];
    for (const k of keys) {
      if (typeof v[k] === "string") return v[k];
    }
    for (const val of Object.values(v)) {
      if (typeof val === "string") return val;
    }
  }
  return "";
}

function getUserLabel(u) {
  return (
    extractStringDeep(u?.name) ||
    extractStringDeep(u?.fullName) ||
    extractStringDeep(u?.identifier) ||
    ""
  );
}

function getUserId(u) {
  const candidates = [u?.id, u?.identifier, u?.member?.identifier, u?.memberId, u?.userId];
  let uid = candidates.find((x) => x !== undefined && x !== null);
  if (typeof uid === "string" && /^\d+$/.test(uid)) uid = Number(uid);
  return uid ?? null;
}

export default function SelectedUsersSummary() {
  const { selectedUserIds, setSelectedUserIds, usersForRoles } = useScheduleData();

  const selectedUsers = useMemo(() => {
    if (!selectedUserIds.length) return [];
    return selectedUserIds.map((uid) => {
      const user = (usersForRoles || []).find((u) => getUserId(u) === uid);
      return { id: uid, name: user ? getUserLabel(user) : String(uid) };
    });
  }, [selectedUserIds, usersForRoles]);

  if (selectedUsers.length === 0) {
    return (
      <div style={{
        padding: "6px 12px",
        marginBottom: 8,
        fontSize: 13,
        color: "var(--white_coordinating, #fff)",
        opacity: 0.7,
        background: "var(--blue_accent, #2a3f5f)",
        borderRadius: 6,
        border: "1px solid rgba(255,255,255,0.08)",
      }}>
        Showing all users (no user filter applied)
      </div>
    );
  }

  function removeUser(uid) {
    setSelectedUserIds((prev) => prev.filter((id) => id !== uid));
  }

  return (
    <div style={{
      padding: "8px 12px",
      marginBottom: 8,
      background: "var(--blue_accent, #2a3f5f)",
      borderRadius: 6,
      border: "1px solid rgba(255,255,255,0.08)",
      color: "var(--white_coordinating, #fff)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Filtered to {selectedUsers.length} user{selectedUsers.length !== 1 ? "s" : ""}:</span>
        <button
          onClick={() => setSelectedUserIds([])}
          style={{
            background: "none",
            border: "1px solid rgba(239,154,154,0.5)",
            color: "#ef9a9a",
            borderRadius: 4,
            padding: "2px 8px",
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          Clear all
        </button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {selectedUsers.map(({ id, name }) => (
          <span
            key={id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              background: "rgba(21, 101, 192, 0.35)",
              border: "1px solid rgba(144, 202, 249, 0.4)",
              borderRadius: 16,
              fontSize: 12,
              color: "#e3f2fd",
            }}
          >
            {name}
            <button
              onClick={() => removeUser(id)}
              aria-label={`Remove ${name} from filter`}
              style={{
                background: "none",
                border: "none",
                color: "#ef9a9a",
                cursor: "pointer",
                padding: 0,
                fontSize: 14,
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
